import type { AssessmentReason, FinancialAvailability } from '@shared/financial-availability';
import { DealFinancialEntryLinks } from './deal-financial-entry-links';

export type FinancialNumeric = number | string | null;

export type FinancialResponseAvailability = Partial<FinancialAvailability> & { historical?: unknown };

/** Old responses are not evidence of a current assessment. */
export function isCurrentAssessment(value: FinancialResponseAvailability | null | undefined): boolean {
  return value?.assessmentStatus === 'assessed' && value.basis != null && value.calculationVersion != null;
}

const metricLabels: Record<string, string> = {
  overallScore: 'Overall score', overallLenderScore: 'Lender score', liquidityScore: 'Liquidity score',
  leverageScore: 'Leverage score', profitabilityScore: 'Profitability score', efficiencyScore: 'Efficiency score',
  growthScore: 'Growth score', serviceabilityScore: 'Serviceability score', coverageScore: 'Coverage score',
  dscrRatio: 'DSCR', dsrPercentage: 'DSR (%)', icrRatio: 'ICR', dscr: 'DSCR', icr: 'ICR',
};
function AvailableMetrics({ value }: { value: unknown }) {
  if (!value || typeof value !== 'object') return null;
  return <dl>{Object.entries(metricLabels).flatMap(([key, label]) => {
    const metric = (value as Record<string, unknown>)[key];
    if (metric == null || metric === '' || (typeof metric !== 'number' && typeof metric !== 'string') || !Number.isFinite(Number(metric))) return [];
    return [<div key={key}><dt>{label}</dt><dd>{Number(metric).toLocaleString('en-AU')}</dd></div>];
  })}</dl>;
}
const reasonMessages: Partial<Record<AssessmentReason, string>> = {
  PERSONAL_DSR_BASIS_UNAPPROVED: 'An approved personal serviceability basis is not available.',
  LEGACY_NO_BASIS: 'The original result has no basis that can be verified.',
  LEGACY_ACK_REQUIRED: 'The historical financial data needs acknowledgement before assessment.',
  STALE_INPUTS: 'The source data changed after this result was calculated.',
  METHOD_UNAVAILABLE: 'An approved method is not available for all required assessment components.',
  NO_PERIOD_SELECTED: 'Select a financial period before assessment.',
  MISSING_FINANCIALS: 'Required financial inputs are missing.',
  PERIOD_UNVERIFIED: 'The selected financial period has not been verified.',
  MISSING_DEBT_PROFILE: 'Verified debt payment inputs are missing.',
  MISSING_INTEREST_BASIS: 'Verified interest inputs are missing.',
  MISSING_SECURITY_BASIS: 'Verified security inputs are missing.',
  PFS_BASIS_MISSING: 'Personal financial inputs are missing.',
  CALC_VERSION_UNSUPPORTED: 'The original calculation version cannot support a current assessment.',
  NO_APPLICABLE_POLICY: 'No applicable lender policy is available for assessment.',
  NOT_CONFIGURED: 'The required financial assessment inputs are not configured.',
};
function HistoricalMetrics({ value }: { value: unknown }) {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const date = record.assessmentDate ?? record.calculationDate ?? record.snapshotDate ?? record.createdAt;
  return <>
    {typeof date === 'string' && <p>Recorded: {new Date(date).toLocaleString('en-AU')}</p>}
    <AvailableMetrics value={value} />
  </>;
}
function HistoricalResult({ value }: { value: unknown }) {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const sources = [['icr', 'Interest coverage'], ['dsr', 'Debt service'], ['healthScore', 'Lender health score']] as const;
  return <details><summary>Historical result (not a current assessment)</summary>
    <HistoricalMetrics value={value} />
    {sources.map(([key, label]) => record[key] != null && typeof record[key] === 'object'
      ? <section key={key}><p>{label}</p><HistoricalMetrics value={record[key]} /></section>
      : null)}
  </details>;
}
export function FinancialUnavailable({ value, action, dealId, accountId }: { value?: FinancialResponseAvailability | null; action: string; dealId?: string; accountId?: string }) {
  return <div role="status">
    <p>Not assessed</p>
    {value?.assessmentStatus === 'failed' && <p>The assessment could not be completed. The current result is unavailable.</p>}
    {value?.assessmentStatus === 'stale' && !value.assessmentReasons?.includes('STALE_INPUTS') && <p>The source data changed after this result was calculated.</p>}
    {value?.assessmentReasons?.map(reason => <p key={reason}>{reasonMessages[reason] ?? 'The required evidence is not ready for assessment.'}</p>)}
    <p>{action}</p>
    {dealId ? <DealFinancialEntryLinks dealId={dealId} /> : accountId && <a className="text-primary hover:underline" href={`/accounts/${accountId}/financials`}>Open financial data</a>}
    {value?.assessmentStatus === 'unassessed' && value.basis != null && <AvailableMetrics value={value} />}
    {value?.historical != null && <HistoricalResult value={value.historical} />}
  </div>;
}
